import { FLAGS } from '@gozoki/feature-flags'
import { useQueries, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { AppTabs, type AppTab } from '~/components/new/app-ui/app-tabs'
import { Title } from '~/components/new/app-ui/title'
import { Card, CardContent } from '~/components/ui/card'
import { Input } from '~/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { Switch } from '~/components/ui/switch'
import { TabsContent } from '~/components/ui/tabs'
import { useFeatureFlag } from '~/hooks/useFeatureFlag'
import { api } from '~/lib/tuyau'
import { withRoutePermissions } from '~/utils/rbac'
import { FULL_HORIZON, HORIZON_OPTIONS, STORE_COLORS, TOTAL_COLOR } from './-previsions-constants'
import { buildAnnualCa, buildCalendarWide, buildCohortWide } from './-previsions-data'
import { CollapsibleCardHeader } from './-collapsible-card'
import { PrevisionsAnnualTable } from './-previsions-annual-table'
import { PrevisionsBacktest } from './-previsions-backtest'
import { PrevisionsChart } from './-previsions-chart'
import { PrevisionsDetail } from './-previsions-detail'
import { PrevisionsParametres, type ForecastOverrides } from './-previsions-parametres'
import { PrevisionsStoreSelector } from './-previsions-store-selector'
import { previsionsSearchSchema, type PrevisionsSearch } from './-search-schema'

export const Route = createFileRoute('/_authenticated/previsions/')({
  validateSearch: previsionsSearchSchema,
  staticData: { permission: ['read', 'previsions.previsions.forecast'] },
  component: PrevisionsPage,
})

withRoutePermissions(Route)

function PrevisionsPage() {
  const enabled = useFeatureFlag(FLAGS.CAForecasting)
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [hidden, setHidden] = useState<Set<string>>(new Set())
  const [applied, setApplied] = useState<ForecastOverrides>({})
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const toggleCollapsed = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const setSearch = (patch: Partial<PrevisionsSearch>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }) })

  const storeIds = search.storeIds ?? []

  const monthlyQuery = applied.monthly?.length === 12 ? { monthly: applied.monthly } : {}
  const dayTypeQuery = {
    ...(applied.weekend != null ? { weekend: applied.weekend } : {}),
    ...(applied.holiday != null ? { holiday: applied.holiday } : {}),
  }
  const poolIds = search.poolIds ?? []
  const poolQuery = poolIds.length > 0 ? { poolStoreIds: poolIds } : {}
  const windowQuery = search.window !== 3 ? { window: search.window } : {}
  const commonQuery = {
    seasonality: search.seasonality,
    ...monthlyQuery,
    ...dayTypeQuery,
    ...poolQuery,
    ...windowQuery,
  }
  const baseDayQuery =
    storeIds.length === 1 && applied.baseDay != null ? { baseDay: applied.baseDay } : {}

  const { data: boutiques = [] } = useQuery(
    api.stores.index.queryOptions({ query: { storeType: 'BOUTIQUE' } }),
  )

  const labelOf = (storeId: number) => {
    const store = boutiques.find((item) => item.id === storeId)
    return store?.shortLabel ?? store?.label ?? `#${storeId}`
  }

  const forecastResults = useQueries({
    queries: storeIds.map((id) =>
      api.forecast.forecastStore.queryOptions(
        {
          params: { id },
          query: { horizon: FULL_HORIZON, hybrid: search.hybrid, ...commonQuery, ...baseDayQuery },
        },
        { enabled },
      ),
    ),
  })

  const backtestQuery = useQuery(
    api.forecast.backtestAggregate.queryOptions(
      { query: { storeIds, horizon: 6, ...commonQuery } },
      { enabled: enabled && storeIds.length > 0 && search.tab === 'backtest' },
    ),
  )

  if (!enabled) {
    return (
      <>
        <AppHeader>
          <Title>Prévisions</Title>
        </AppHeader>
        <AppContainer>
          <p className="text-muted-foreground">Fonctionnalité non activée.</p>
        </AppContainer>
      </>
    )
  }

  const forecasts = forecastResults
    .map((result) => result.data)
    .filter((data): data is NonNullable<typeof data> => data != null && data.baseDay != null)
  const isForecastPending =
    storeIds.length > 0 && forecastResults.some((result) => result.isPending)
  const referenceForecast = forecasts[0]
  const totalExecutionMs = forecasts.reduce((sum, forecast) => sum + forecast.executionMs, 0)
  const seasonalityFallback = forecasts.some((forecast) => forecast.seasonalityFallback)

  const chartForecasts = forecasts.map((forecast) => ({
    ...forecast,
    points: forecast.points.slice(0, search.horizon),
  }))
  const annual = buildAnnualCa(forecasts)

  const { rows, storeKeys } =
    search.view === 'cohorte' ? buildCohortWide(chartForecasts) : buildCalendarWide(chartForecasts)
  const showTotal = storeKeys.length > 1
  const totalLabel = search.view === 'cohorte' ? 'Moyenne' : 'Total'

  const chartConfig = Object.fromEntries([
    ...storeKeys.flatMap((sk, index) => {
      const color = STORE_COLORS[index % STORE_COLORS.length]
      return [
        [sk.realKey, { label: labelOf(sk.storeId), color }],
        [sk.forecastKey, { label: `${labelOf(sk.storeId)} (prévu)`, color }],
      ]
    }),
    ['total', { label: totalLabel, color: TOTAL_COLOR }],
    ['totalf', { label: `${totalLabel} (prévu)`, color: TOTAL_COLOR }],
  ])

  const toggle = (dataKey: string) =>
    setHidden((prev) => {
      const next = new Set(prev)
      if (next.has(dataKey)) next.delete(dataKey)
      else next.add(dataKey)
      return next
    })

  const backtest = backtestQuery.data

  const singleForecast = chartForecasts.length === 1 ? chartForecasts[0] : null

  const tabs: AppTab<'forecast' | 'backtest'>[] = [
    { value: 'forecast', label: 'Prévision' },
    { value: 'backtest', label: 'Backtest' },
  ]

  return (
    <>
      <AppHeader>
        <Title>Prévisions</Title>
      </AppHeader>
      <AppContainer className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">Boutiques à prédire</span>
            <PrevisionsStoreSelector
              title="Boutiques à prédire"
              selectedIds={storeIds}
              onApply={(ids) => {
                setApplied((prev) => ({ ...prev, baseDay: undefined }))
                setSearch({ storeIds: ids.length > 0 ? ids : undefined })
              }}
              placeholder="Choisir des boutiques"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">Réseau de référence (fit)</span>
            <PrevisionsStoreSelector
              title="Réseau de référence (fit)"
              selectedIds={poolIds}
              onApply={(ids) => setSearch({ poolIds: ids.length > 0 ? ids : undefined })}
              placeholder="Tout le réseau"
              copyStoreIds={storeIds}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">Horizon</span>
            <Select
              value={search.horizon.toString()}
              onValueChange={(value) => setSearch({ horizon: Number(value) })}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HORIZON_OPTIONS.map((horizon) => (
                  <SelectItem key={horizon} value={horizon.toString()}>
                    {horizon} mois
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">Vue</span>
            <Select
              value={search.view}
              onValueChange={(value) =>
                setSearch({ view: value === 'cohorte' ? 'cohorte' : 'calendaire' })
              }
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="calendaire">Calendaire</SelectItem>
                <SelectItem value="cohorte">Cohorte (ancienneté)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pb-2">
            <Switch
              id="seasonality"
              checked={search.seasonality}
              onCheckedChange={(checked) => setSearch({ seasonality: checked })}
            />
            <label htmlFor="seasonality" className="text-sm">
              Saisonnalité
            </label>
          </div>
          <div className="flex items-center gap-2 pb-2">
            <Switch
              id="hybrid"
              checked={search.hybrid}
              onCheckedChange={(checked) => setSearch({ hybrid: checked })}
            />
            <label
              htmlFor="hybrid"
              className="text-sm"
              title="Mois en cours : CA réel des jours écoulés + prévision des jours restants. Désactivé : mois en cours prédit en entier."
            >
              Mois en cours hybride
            </label>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">Fenêtre Base_jour (mois)</span>
            <Input
              key={search.window}
              type="number"
              step="1"
              min="1"
              max="24"
              className="w-28"
              defaultValue={search.window}
              title="Nombre de derniers mois observés utilisés pour mesurer la Base_jour (médiane des mois déshabillés)."
              onBlur={(event) => {
                const raw = event.target.value
                setSearch({ window: raw === '' ? 3 : Math.min(24, Math.max(1, Number(raw))) })
              }}
            />
          </div>
        </div>

        {seasonalityFallback && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
            Réseau de référence trop restreint pour estimer la saisonnalité : les 12 coefficients
            mensuels ont été repris sur tout le réseau. Élargissez le réseau de référence pour une
            saisonnalité propre au groupe.
          </div>
        )}

        {referenceForecast?.tables != null && (
          <Card>
            <CollapsibleCardHeader
              collapsed={collapsed.has('params')}
              onToggle={() => toggleCollapsed('params')}
            >
              Paramètres de calcul
            </CollapsibleCardHeader>
            {!collapsed.has('params') && (
              <CardContent>
                <PrevisionsParametres
                  fitted={referenceForecast.tables}
                  applied={applied}
                  onApply={setApplied}
                  singleStore={forecasts.length === 1}
                  effectiveBaseDay={singleForecast?.baseDay ?? null}
                />
              </CardContent>
            )}
          </Card>
        )}

        <AppTabs tabs={tabs} value={search.tab} onTabChange={(tab) => setSearch({ tab })}>
          <TabsContent value="forecast">
            {(() => {
              if (storeIds.length === 0) {
                return (
                  <p className="text-muted-foreground">Sélectionnez une ou plusieurs boutiques.</p>
                )
              }
              if (isForecastPending) {
                return <p className="text-muted-foreground">Calcul en cours…</p>
              }
              if (forecasts.length === 0) {
                return (
                  <p className="text-muted-foreground">
                    Pas assez de données pour les boutiques sélectionnées.
                  </p>
                )
              }
              return (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    <span>{forecasts.length} boutique(s)</span>
                    <span>Panel de fit : {referenceForecast?.poolSize} boutiques</span>
                    <span>Calcul : {totalExecutionMs} ms</span>
                  </div>
                  <PrevisionsChart
                    view={search.view}
                    rows={rows}
                    storeKeys={storeKeys}
                    chartConfig={chartConfig}
                    hidden={hidden}
                    onToggle={toggle}
                    showTotal={showTotal}
                    totalLabel={totalLabel}
                    labelOf={labelOf}
                  />

                  <PrevisionsAnnualTable
                    annual={annual}
                    labelOf={labelOf}
                    showTotal={showTotal}
                    collapsed={collapsed.has('annual')}
                    onToggle={() => toggleCollapsed('annual')}
                  />

                  {singleForecast != null && (
                    <PrevisionsDetail
                      forecast={singleForecast}
                      label={labelOf(singleForecast.storeId)}
                      collapsed={collapsed.has('detail')}
                      onToggle={() => toggleCollapsed('detail')}
                    />
                  )}
                </div>
              )
            })()}
          </TabsContent>

          <TabsContent value="backtest">
            <PrevisionsBacktest
              storeCount={storeIds.length}
              isPending={backtestQuery.isPending}
              backtest={backtest}
            />
          </TabsContent>
        </AppTabs>
      </AppContainer>
    </>
  )
}
